import React from 'react';

import {
  Formik,
  Field,
  ErrorMessage,
  FieldProps,
  FormikProps,
  FieldInputProps
} from 'formik';
import * as Yup from 'yup';
import { Button, Form, Image } from 'react-bootstrap';

import RefreshImg from '../../../assets/refresh.svg';

interface ICustomFormComponentProps<T> {
  values: T;
  handleSubmit: (e: any) => void;
  handleChange: (fieldName: string, fieldValue: any) => void;
  handleChangeSilent: (fieldName: string, fieldValue: any) => void;
  handleBlur: (e: any) => void;
  isInvalid: (fieldName: string) => boolean | undefined;
  syncField: (fieldName: string) => void;
}

interface ICustomFormProps<T> {
  validationSchema: Yup.SchemaOf<any>;
  initialValues: T;
  forwardedRef: React.RefObject<FormikProps<T>>;
  onSubmit: (values: T) => void;
  handleFormChange: (field: string, value: any) => void;
  component: (props: ICustomFormComponentProps<T>) => JSX.Element;
}

export const CustomForm = <T extends Record<string, any>>({
  validationSchema,
  initialValues,
  forwardedRef,
  onSubmit,
  handleFormChange,
  component
}: ICustomFormProps<T>) => {
  return (
    <Formik
      innerRef={forwardedRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        dirty,
        touched,
        submitCount,
        handleBlur,
        handleSubmit,
        setFieldValue
      }) => {
        const isInvalid = (name: string) =>
          (touched[name] || submitCount > 0) && !!errors[name];
        const updateFormState = (fieldName: string, fieldValue: any) => {
          setTimeout(() => handleFormChange(fieldName, fieldValue), 0);
        };
        const syncField = (fieldName: string) => {
          if (!fieldName) return;
          updateFormState(fieldName, values[fieldName]);
        };
        const handleChange = (fieldName: string, fieldValue: any) => {
          setFieldValue(fieldName, fieldValue);
          updateFormState(fieldName, fieldValue);
        };
        const handleChangeSilent = (fieldName: string, fieldValue: any) => {
          setFieldValue(fieldName, fieldValue);
        };
        const handleBlurWithSync = (event: any) => {
          handleBlur(event);
          const fieldName = event?.target?.name;
          if (!fieldName) return;
          setTimeout(() => syncField(fieldName), 0);
        };

        return component({
          values,
          handleChange,
          handleChangeSilent,
          handleBlur: handleBlurWithSync,
          handleSubmit,
          isInvalid,
          syncField
        });
      }}
    </Formik>
  );
};

export const FormItem: React.FC<{
  name: string;
  label: string;
  failRequest?: boolean;
  onPressedRefresh?: () => void;
  component: (field: FieldInputProps<any>) => JSX.Element;
}> = ({ name, label, component, failRequest, onPressedRefresh }) => {
  return (
    <Form.Group controlId={name} key={name}>
      <Field name={name}>
        {({ field, form }: FieldProps) => {
          const { error, touched } = form.getFieldMeta(name);
          const hasError =
            (touched || form.dirty || form.submitCount > 0) && !!error;

          return (
            <div data-form-field={name} data-form-label={label}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Form.Label
                  className={hasError ? 'form-label-invalid' : undefined}
                >
                  {label}
                </Form.Label>
                {failRequest ? (
                  <Button
                    style={{
                      marginLeft: '20px',
                      marginBottom: '5px',
                      padding: '2px',
                      backgroundColor: 'transparent',
                      borderColor: 'transparent'
                    }}
                    onClick={onPressedRefresh}
                  >
                    <Image src={RefreshImg} />
                  </Button>
                ) : null}
              </div>
              {component(field)}
              <ErrorMessage
                render={msg => (
                  <Form.Control.Feedback type="invalid">
                    {msg}
                  </Form.Control.Feedback>
                )}
                name={name}
              />
            </div>
          );
        }}
      </Field>
    </Form.Group>
  );
};
